import { useMemo, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { parseNumberFilter } from '@/utils/filters';
import type { UserFilters } from '../types/filters.types';
import type { UserListSearch } from '../types/user.types';

interface UseUserListFiltersArgs {
  userTypeIds: readonly number[];
  showAllTypes: boolean;
}

export function useUserListFilters({ userTypeIds, showAllTypes }: UseUserListFiltersArgs) {
  const defaultUserTypeId = showAllTypes ? undefined : userTypeIds;

  const [searchText, setSearchText] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<UserFilters>({});
  const [filterOpen, setFilterOpen] = useState(false);

  const { draftValues, setDraftValues, applyValues, hasPendingChanges } = useFilterDraft({
    appliedValues: appliedFilters,
    onApply: setAppliedFilters,
    shouldWaitForApply: true,
  });

  const debouncedSearchText = useDebounce(searchText);

  const userTypeId = parseNumberFilter(appliedFilters.userTypeId) ?? defaultUserTypeId;

  const queryParams = useMemo<UserListSearch>(
    () => ({
      userTypeId,
      search: debouncedSearchText || undefined,
      status: (appliedFilters.status as UserListSearch['status']) || undefined,
      companyId: parseNumberFilter(appliedFilters.companyId),
      departmentId: parseNumberFilter(appliedFilters.departmentId),
      designationId: parseNumberFilter(appliedFilters.designationId),
    }),
    [userTypeId, debouncedSearchText, appliedFilters],
  );

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;
  const selectedCompanyId = parseNumberFilter(draftValues.companyId);

  return {
    searchText,
    filters: draftValues,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    selectedCompanyId,
    queryParams,
    handleSearchChange: setSearchText,
    handleFilterChange: setDraftValues,
    applyFilterValues: applyValues,
  };
}
